// Hand-rolled hash router. ~1 KB. Routes lazy-load.
// Svelte 5 runes module — must be .svelte.ts.

import type { Component } from 'svelte';
import { start as progressStart, stop as progressStop } from '$domain/progress.svelte';

type LazyComponent = Component<Record<string, never>>;

type RouteDef = {
  path: string;
  load: () => Promise<{ default: LazyComponent }>;
};

const routes: RouteDef[] = [];

function read(): { path: string; query: string } {
  if (typeof window === 'undefined') return { path: '/', query: '' };
  const h = window.location.hash.slice(1);
  if (h.length === 0) return { path: '/', query: '' };
  const q = h.indexOf('?');
  if (q === -1) return { path: h, query: '' };
  return { path: h.slice(0, q), query: h.slice(q + 1) };
}

const init = read();
let _path: string = $state(init.path);
let _query: string = $state(init.query);
let _component: LazyComponent | null = $state(null);

async function resolve(): Promise<void> {
  if (routes.length === 0) return;
  const match = routes.find((r) => r.path === _path) ?? routes[0]!;
  // Surface the lazy-load in the global progress bar. Most splits are
  // cached after first hit; the bar only flashes on cold loads which is
  // exactly when the user wants the feedback.
  progressStart();
  try {
    const mod = await match.load();
    _component = mod.default;
  } finally {
    progressStop();
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', () => {
    const next = read();
    const pathChanged = next.path !== _path;
    _path = next.path;
    _query = next.query;
    if (pathChanged) void resolve();
  });
}

export function defineRoutes(rs: RouteDef[]): void {
  routes.splice(0, routes.length, ...rs);
  void resolve();
}

export function navigate(path: string): void {
  if (typeof window !== 'undefined') window.location.hash = path;
}

export const route = {
  get path() {
    return _path;
  },
  get query() {
    return _query;
  },
  get component() {
    return _component;
  },
};
